import React, { useEffect, useState } from 'react';
import { RentalForm } from './components/RentalForm';
import { RentalSummary } from './components/RentalSummary';
import { Logo } from './components/Logo';
import { DiscountBanner } from './components/DiscountBanner';
import { PendingRentals } from './components/PendingRentals';
import { AdminPanel } from './components/AdminPanel';
import { usePrices } from './contexts/PriceContext';
import { useEmail } from './contexts/EmailContext';
import { Link } from 'react-router-dom';
import { SettingsIcon, ShieldCheck, PhoneIcon, MailIcon } from 'lucide-react';
export function App() {
  const {
    prices
  } = usePrices();
  const {
    email
  } = useEmail();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const handleAdminClick = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      setShowAdminPanel(true);
    } else {
      const button = e.currentTarget as HTMLElement;
      button.classList.add('shake');
      setTimeout(() => button.classList.remove('shake'), 500);
    }
  };
  const initialSelections = {
    gen3Body: 0,
    gen3Head: 0,
    gen3JudgesOnly: 0,
    fullGen3Ring: 0,
    requireTech: false
  };
  const [selections, setSelections] = useState(initialSelections);
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [isCaliforniaAddress, setIsCaliforniaAddress] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandPendingRentals, setExpandPendingRentals] = useState(false);
  const [discountSelections, setDiscountSelections] = useState({
    collegiate: false
  });
  useEffect(() => {
    const bodyPrice = selections.gen3Body * prices.gen3Body;
    const headPrice = selections.gen3Head * prices.gen3Head;
    const judgesPrice = selections.gen3JudgesOnly * prices.gen3JudgesOnly;
    const fullRingPrice = selections.fullGen3Ring * prices.fullGen3Ring;
    const techPrice = selections.requireTech ? prices.technician : 0;
    const calculatedSubtotal = bodyPrice + headPrice + judgesPrice + fullRingPrice + techPrice;
    setSubtotal(calculatedSubtotal);
    const calculatedTax = isCaliforniaAddress ? calculatedSubtotal * prices.taxRate : 0;
    setTax(calculatedTax);
    setTotal(calculatedSubtotal + calculatedTax);
  }, [selections, isCaliforniaAddress, prices]);
  const handleSelectionChange = (name: string, value: number | boolean) => {
    setSelections(prev => {
      if (name === 'gen3Head' && typeof value === 'number') {
        const totalBodies = prev.gen3Body + prev.fullGen3Ring;
        if (value > totalBodies) {
          return prev;
        }
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };
  const handleTechToggle = () => {
    setSelections(prev => ({
      ...prev,
      requireTech: !prev.requireTech
    }));
  };
  const handleCaliforniaAddressChange = (isCaliforniaAddress: boolean) => {
    setIsCaliforniaAddress(isCaliforniaAddress);
  };
  const handleDiscountChange = (discounts: {
    collegiate: boolean;
  }) => {
    setDiscountSelections(discounts);
  };
  const handleRentalSubmitted = () => {
    setRefreshKey(prev => prev + 1);
    setSelections(initialSelections);
    setIsCaliforniaAddress(false);
    setDiscountSelections({
      collegiate: false
    });
    setExpandPendingRentals(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-slate-100" data-id="element-0">
      <style data-id="element-1">{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>

      <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-2">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-3">
          <div className="flex items-center gap-4" data-id="element-4">
            <div className="text-slate-700" data-id="element-5">
              <div className="flex items-center mb-1" data-id="element-6">
                <PhoneIcon size={16} className="mr-2 text-red-600" data-id="element-7" />
                <span className="text-sm font-medium" data-id="element-8">(517) 294-0315</span>
              </div>
              <div className="flex items-center" data-id="element-9">
                <MailIcon size={16} className="mr-2 text-red-600" data-id="element-10" />
                <span className="text-sm font-medium" data-id="element-11">shane@tkdscore.com</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-12">
            <button onClick={handleAdminClick} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-13">
              <ShieldCheck size={20} className="text-slate-400 hover:text-blue-600 transition-colors" data-id="element-14" />
            </button>

            {email === 'shane@tkdscore.com' && <Link to="/config" className="flex items-center text-slate-600 hover:text-blue-800" title="Configure Prices" data-id="element-15">
                <SettingsIcon size={18} className="mr-1" data-id="element-16" />
                <span className="hidden sm:inline" data-id="element-17">Configure Prices</span>
              </Link>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6" data-id="element-18">
        <header className="mb-8 mt-6" data-id="element-19">
          <h1 className="text-3xl font-bold text-slate-800" data-id="element-20">
            Gen 3 Scoring System Rental
          </h1>
          <p className="text-slate-600 mt-2" data-id="element-21">
            Select the components you need for your event
          </p>
        </header>

        <div className="flex gap-6 mb-8" data-id="element-22">
          <div className="flex-shrink-0" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/jAaJbUwB1t1dUCrFYJwyZT/Unknown_9.jpg" alt="TrueScore & USA TKD" className="h-full w-auto object-contain" style={{
            maxWidth: '300px'
          }} data-id="element-24" />
          </div>

          <div className="flex-1 space-y-6" data-id="element-25">
            <PendingRentals refreshKey={refreshKey} shouldExpand={expandPendingRentals} onExpandChange={setExpandPendingRentals} data-id="element-26" />

            <DiscountBanner onDiscountChange={handleDiscountChange} data-id="element-27" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-28">
          <div className="lg:col-span-2" data-id="element-29">
            <RentalForm selections={selections} onSelectionChange={handleSelectionChange} onTechToggle={handleTechToggle} prices={prices} data-id="element-30" />
          </div>
          <div className="lg:col-span-1" data-id="element-31">
            <RentalSummary selections={selections} subtotal={subtotal} tax={tax} total={total} onCaliforniaAddressChange={handleCaliforniaAddressChange} prices={prices} onRentalSubmitted={handleRentalSubmitted} discountSelections={discountSelections} data-id="element-32" />
          </div>
        </div>
      </div>

      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} data-id="element-33" />}
    </div>;
}